import {Injectable, TemplateRef} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {RoleService} from 'app/blocks/service/api/role.service';
import {NzModalRef, NzModalService} from 'ng-zorro-antd/modal';
import {DiMobileActionSheetComponent, MobileSheetAction, MobileSheetData} from 'app/common/mobile/di-mobile-action-sheet.component';
import {DiMobileFilterSheetComponent, MobileFilterSheetData, MobileFilterSheetResult} from 'app/common/mobile/di-mobile-filter-sheet.component';
import {MobileViewportService} from 'app/common/mobile/mobile-viewport.service';
import {DiMobileSortComponent, MobileSortData} from 'app/common/mobile/di-mobile-sort.component';

/**
 * Opens the phone-only overlays the mobile layer needs. Each one is an nz-modal, which _mobile-shell.scss
 * already turns into a bottom sheet, so nothing here positions anything.
 */
@Injectable({providedIn: 'root'})
export class MobileActionSheetService {
    constructor(
        private readonly _modalService: NzModalService,
        private readonly _router: Router,
        private readonly _viewport: MobileViewportService,
        private readonly _translate: TranslateService,
        private readonly _roleService: RoleService
    ) {}

    /** A list of actions: the phone stand-in for a dropdown, a kebab menu or a right-click menu. */
    open(data: MobileSheetData): NzModalRef<DiMobileActionSheetComponent> {
        return this._modalService.create({
            nzContent: DiMobileActionSheetComponent,
            nzData: data,
            nzFooter: null,
            nzClosable: false,
            nzMaskClosable: true,
            nzClassName: 'di-msheet-modal'
        });
    }

    /**
     * A row's "more" menu as a sheet, from the array the screen already passes to di-entity-action-menu.
     *
     * The desktop menu drops any action whose isEnabledFor is false; the sheet keeps it, greyed, because on
     * touch a silently missing row reads as a bug rather than as "not available here" (R5). Labels resolve
     * through the same key the menu template builds.
     */
    openEntityActions(actions: any[], entity: any, resourceName: string, title?: string): NzModalRef<DiMobileActionSheetComponent> {
        const rows: MobileSheetAction[] = (actions || []).map((act) => ({
            label: this._translate.instant(`${resourceName}.pages.list.rowActions.${act.label}`),
            disabled: act.isEnabledFor ? !act.isEnabledFor(entity) : false,
            run: () => act.action(entity)
        }));
        return this.open({title, actions: rows});
    }

    /**
     * The header Actions menu as a sheet, from `headerParams.groupActions` - the same array
     * di-list-page-header renders.
     *
     * That array is not flat: an entry with `subActions` is a submenu, and the sheet turns the parent's
     * label into the heading over its children, so the invoice screen's 18 bulk actions arrive in the
     * Operations / Bulk Operations / Export groups the desktop shows rather than as one run-on list.
     *
     * Three things are copied from the desktop template rather than reinvented, so the two cannot drift:
     * visibility comes from the same roleShow key, enablement from the same `enabled$`, and an action runs
     * only when enabled, which is what di-list-page-header's executeAction does.
     */
    openHeaderActions(actions: any[], resourceName: string, options: {title?: string} = {}): NzModalRef<DiMobileActionSheetComponent> {
        const key = (act: any): string => `${resourceName}.pages.list.groupActions.${act.label}`;
        const toRow = (act: any, group?: string): MobileSheetAction => ({
            label: this._translate.instant(key(act)) + (act.dynamicValue ? ` (${act.dynamicValue})` : ''),
            group,
            disabled: !this._readSync<boolean>(act.enabled$, true),
            run: () => {
                if (act.action != null) {
                    act.action();
                }
            }
        });

        const rows: MobileSheetAction[] = [];
        (actions || []).forEach((act) => {
            if (act.subActions?.length) {
                const heading = this._translate.instant(key(act));
                act.subActions.filter((sub) => this._roleService.showSetting(key(sub))).forEach((sub) => rows.push(toRow(sub, heading)));
            } else if (this._roleService.showSetting(key(act))) {
                rows.push(toRow(act));
            }
        });

        return this.open({title: options.title, actions: rows});
    }

    /**
     * Reads the current value of an observable without waiting.
     *
     * Every `enabled$` in the action arrays is a BehaviorSubject or a map over one, so it has a value the
     * moment it is subscribed and this returns it. The fallback is what an action gets if some future
     * `enabled$` turns out to be async: enabled, matching the desktop, where an action nobody has disabled
     * is clickable. It never waits, so a stream that does not emit cannot stop the sheet from opening.
     */
    private _readSync<T>(source: Observable<T> | undefined, fallback: T): T {
        if (!source) {
            return fallback;
        }
        let value = fallback;
        source
            .pipe(take(1))
            .subscribe((emitted) => {
                value = emitted;
            })
            .unsubscribe();
        return value;
    }

    /**
     * A screen's filter fields as a sheet, with Apply and Clear.
     *
     * The table's thead is not rendered on a phone, so the column filter icons that normally carry these
     * fields are gone. The template is the screen's own, so the fields keep binding to the same filter
     * state; the caller decides what Apply and Clear do:
     *
     *   this._sheets.openFilters(this.mobileFilters, {title})
     *       .afterClose.subscribe((result) => {
     *           if (result === 'clear') { this.clearFilters(); }
     *           if (result) { this.getEntityList(); }
     *       });
     */
    openFilters(
        template: TemplateRef<any>,
        options: {title?: string; context?: any} = {}
    ): NzModalRef<DiMobileFilterSheetComponent, MobileFilterSheetResult> {
        const data: MobileFilterSheetData = {template, title: options.title, context: options.context};
        return this._modalService.create({
            nzContent: DiMobileFilterSheetComponent,
            nzData: data,
            nzFooter: null,
            nzClosable: false,
            nzMaskClosable: true,
            // Not di-msheet-modal: that strips the body padding so action rows can run edge to edge, which
            // is wrong for form fields. di-mobile-ready gives the di-* controls the label-above layout.
            nzClassName: 'di-mfilter-modal di-mobile-ready'
        });
    }

    /**
     * The list's "Sort by" as a sheet, from the sort icon beside the search box (MobileTableSortDirective).
     * Sorting applies as it is chosen, like a header click on the desktop; Done just closes the sheet.
     */
    openSort(data: MobileSortData): NzModalRef<DiMobileSortComponent> {
        return this._modalService.create({
            nzContent: DiMobileSortComponent,
            nzData: data,
            nzFooter: null,
            nzClosable: false,
            nzMaskClosable: true,
            nzClassName: 'di-mfilter-modal di-mobile-ready'
        });
    }

    /**
     * Any existing ng-template as a sheet - for content the desktop shows in a popover anchored to a small
     * icon (order breakdown, customer breakdown, filter popovers), which has no room at 390px. The template
     * is the desktop one, so its bindings and handlers stay the same.
     *
     * `di-mobile-ready` is set so tables and read-only fields inside it render as phone cards.
     */
    openTemplate(content: TemplateRef<any>, title?: string): NzModalRef {
        return this._modalService.create({
            nzTitle: title,
            nzContent: content,
            nzFooter: null,
            nzClassName: 'di-mobile-ready'
        });
    }

    /**
     * Open an in-app page. The desktop opens orders, runs and invoices in new windows so a planner can keep
     * the list; on a phone that stacks browser tabs and loses the back button, so the page opens in place.
     */
    openInApp(url: string): void {
        if (this._viewport.isPhone()) {
            this._router.navigateByUrl(url);
        } else {
            window.open(url, '_blank');
        }
    }

    /**
     * Open a document (a PDF, a generated report). Deliberately NOT openInApp: a PDF is not an Angular
     * route, and navigating the router at it would leave the user on a blank page. Every mobile browser
     * has its own viewer, with its own share and download, so the tab is the right answer on a phone too.
     *
     * Decision D4. It exists so a screen reaching for openInApp on a PDF link finds this instead.
     */
    openDocument(url: string): void {
        window.open(url, '_blank');
    }

    /**
     * Whether a popup-handshake flow should be offered at all.
     *
     * A few flows open a sized popup that talks back to its opener - "Add Windowed"
     * (`?hideSideBar=true` at a fixed 1500x1080), "Add To Run" (localStorage plus `window.close()`), the
     * simple-list pickers. A phone browser has no second window to size, ignores the dimensions, and
     * `window.close()` on a tab it did not script-open does nothing, so the user is stranded. Hide the
     * entry point rather than let it half-work (D4); the same work is reachable through the normal screens.
     */
    get popupFlowsAvailable(): boolean {
        return !this._viewport.isPhone();
    }
}
